import type { IPublicClientApplication } from '@azure/msal-browser'
import { createContext, lazy, Suspense, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  type DataRouter,
  type RouteObject,
} from 'react-router'
import { RouterProvider } from 'react-router/dom'

import { features as registeredFeatures } from '@/app/features'
import { PassThrough, PassThroughBoundary } from '@/features/auth/PassThrough'
import type { SignInFailure } from '@/features/auth/sign-in-failure'
import { AuthProvider } from '@/shared/auth/auth-context'
import { forgetSignInFailure } from '@/shared/auth/kept-sign-in-failure'
import { usePublicPage } from '@/shared/auth/public-page'
import {
  hopPagePath,
  hopSignOutPath,
  isRoutePath,
  isSignedInThroughHop,
  readPendingHop,
  readStoredSignOutReturn,
  signInLinkPath,
} from '@/shared/auth/sign-in-hop'
import { RuntimeConfigProvider } from '@/shared/config/runtime-config-context'
import type { RuntimeConfig } from '@/shared/config/runtime-config'
import { FeatureFlagRefresh } from '@/shared/feature-flags'
import { useHoldAddress } from '@/shared/layout/address-hold'
import { AppShell, PageFallback } from '@/shared/layout/AppShell'
import { RouteTitle } from '@/shared/layout/page-title'
import { PageBoundary, RoutePage } from '@/shared/layout/RoutePage'
import { buildRegistry, FeatureProviders, RegistryProvider, type Feature } from '@/shared/registry'

// The callback's path comes from `/config.json`, so its route is the shell's, not the registry's.
const AuthCallbackPage = lazy(async () => ({
  default: (await import('@/features/auth/AuthCallbackPage')).AuthCallbackPage,
}))
const SignInFailedPage = lazy(async () => ({
  default: (await import('@/features/auth/SignInFailedPage')).SignInFailedPage,
}))
// The sign-in site's pages another site's sign-in and sign-out pass through (SM-04): outside the shell.
const SignInHopPage = lazy(async () => ({
  default: (await import('@/features/auth/SignInHopPage')).SignInHopPage,
}))
const SignOutHopPage = lazy(async () => ({
  default: (await import('@/features/auth/SignOutHopPage')).SignOutHopPage,
}))
const SignInLinkPage = lazy(async () => ({
  default: (await import('@/features/auth/SignInLinkPage')).SignInLinkPage,
}))

/**
 * The page a one-time sign-in link opens (SM-39): public, as nobody is signed in yet, and kept at its
 * address, as a move to the site's primary one would lose the link before it is read.
 */
function SignInLinkRoute() {
  usePublicPage(true)
  useHoldAddress()

  return (
    <RouteTitle titleId="auth.callback.completing">
      <PageBoundary>
        <Suspense fallback={<PageFallback />}>
          <SignInLinkPage />
        </Suspense>
      </PageBoundary>
    </RouteTitle>
  )
}

/**
 * The failure page, public from before its chunk loads, so nothing on it sends anyone to B2C. It
 * keeps its address while shown: a move to the site's primary one would reload it and lose the failure.
 */
function SignInFailedRoute(props: { readonly failure: SignInFailure }) {
  usePublicPage(true)
  useHoldAddress()

  return (
    <RouteTitle titleId="auth.callback.failed.title">
      <PageBoundary>
        <Suspense fallback={<PageFallback />}>
          <SignInFailedPage failure={props.failure} />
        </Suspense>
      </PageBoundary>
    </RouteTitle>
  )
}

/** The failure the callback shows, until the address leaves the callback. */
const SignInFailureContext = createContext<{ readonly failure: SignInFailure | undefined; readonly dismiss: () => void }>({
  failure: undefined,
  dismiss: () => undefined,
})

/** The callback: the failure B2C's answer left, else the page that finishes the sign-in. */
function CallbackRoute() {
  const { failure } = useContext(SignInFailureContext)
  if (failure !== undefined) {
    return <SignInFailedRoute failure={failure} />
  }
  return (
    <RouteTitle titleId="auth.callback.completing">
      <PageBoundary>
        <AuthCallbackPage />
      </PageBoundary>
    </RouteTitle>
  )
}

/**
 * Forgets the failure once the address leaves the callback, so a later visit there, as the sign-in
 * guard makes to start the session, finishes the sign-in rather than show the old failure.
 */
function DismissSignInFailureAway(props: { readonly redirectPath: string }) {
  const { failure, dismiss } = useContext(SignInFailureContext)
  const isAway = !isRoutePath(props.redirectPath, useLocation().pathname)
  useEffect(() => {
    if (failure !== undefined && isAway) {
      dismiss()
    }
  }, [failure, isAway, dismiss])
  return null
}

/**
 * A page another site's sign-in or sign-out passes through on the sign-in site: public, and with no
 * header, navigation or footer, as the sign-in site shows no page of its own (SM-04). A failure,
 * as of a chunk that could not load, sends the person on rather than show an error page.
 */
function PassThroughRoute(props: { readonly config: RuntimeConfig; readonly msalInstance: IPublicClientApplication | null; readonly children: ReactNode }) {
  usePublicPage(true)

  return (
    <RouteTitle titleId="auth.callback.completing">
      <PassThroughBoundary apiBaseUrl={props.config.apiBaseUrl} redirectPath={props.config.auth.redirectPath} msalInstance={props.msalInstance}>
        <Suspense fallback={<PassThrough />}>{props.children}</Suspense>
      </PassThroughBoundary>
    </RouteTitle>
  )
}

/**
 * Builds the app from the feature registry (`src/app/features.ts`): each registered route is its
 * own lazily loaded chunk, behind the sign-in guard unless it is public. Protection is declared
 * on the route, never by its URL. `features` is for tests; the app always uses the registry.
 * `signInFailure` is B2C's answer that could not be used, shown at the callback in the shell.
 */
export default function App(props: {
  readonly config: RuntimeConfig
  readonly msalInstance: IPublicClientApplication | null
  readonly features?: readonly Feature[]
  readonly signInFailure?: SignInFailure | undefined
}) {
  const features = props.features ?? registeredFeatures
  const [signInFailure, setSignInFailure] = useState(props.signInFailure)
  const failureContext = useMemo(
    () => ({
      failure: signInFailure,
      dismiss: () => {
        forgetSignInFailure()
        setSignInFailure(undefined)
      },
    }),
    [signInFailure],
  )
  const { redirectPath } = props.config.auth
  // On the sign-in site, the callback another site's sign-in or sign-out comes back through.
  const [isPassThrough] = useState(
    () => !isSignedInThroughHop(props.config.auth) && (readPendingHop() !== null || readStoredSignOutReturn() !== null),
  )
  // The shell's own routes, which no feature may take.
  const registry = useMemo(
    () => buildRegistry(features, [redirectPath, '/index.html', hopPagePath, hopSignOutPath, signInLinkPath]),
    [features, redirectPath],
  )
  // A data router, for `useBlocker` (unsaved changes, GW-21); the routes stay declarative.
  const routes = useMemo<RouteObject[]>(
    () => [
      {
        path: '*',
        element: (
          <FeatureProviders>
            {/* Under the features' providers, so a token redirect it starts waits for any hold they set. */}
            <FeatureFlagRefresh />
            <DismissSignInFailureAway redirectPath={redirectPath} />
            <Routes>
              <Route
                path={hopPagePath}
                element={
                  <PassThroughRoute config={props.config} msalInstance={props.msalInstance}>
                    <SignInHopPage />
                  </PassThroughRoute>
                }
              />
              <Route
                path={hopSignOutPath}
                element={
                  <PassThroughRoute config={props.config} msalInstance={props.msalInstance}>
                    <SignOutHopPage />
                  </PassThroughRoute>
                }
              />
              {isPassThrough && (
                <Route
                  path={redirectPath}
                  element={
                    <PassThroughRoute config={props.config} msalInstance={props.msalInstance}>
                      <AuthCallbackPage />
                    </PassThroughRoute>
                  }
                />
              )}
              <Route element={<AppShell />}>
                {registry.routes.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<RoutePage key={route.path} route={route} />}
                  />
                ))}
                <Route path={redirectPath} element={<CallbackRoute />} />
                <Route path={signInLinkPath} element={<SignInLinkRoute />} />
                <Route path="/index.html" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </FeatureProviders>
        ),
      },
    ],
    [registry, redirectPath, isPassThrough, props.config, props.msalInstance],
  )
  // Made and disposed in an effect, not in render, so no router is left listening to history.
  const [router, setRouter] = useState<DataRouter | null>(null)
  useEffect(() => {
    const created = createBrowserRouter(routes)
    // oxlint-disable-next-line react/set-state-in-effect -- the router listens to history, so it is made where it can be disposed
    setRouter(created)
    return () => {
      created.dispose()
    }
  }, [routes])

  return (
    <RuntimeConfigProvider config={props.config}>
      <AuthProvider config={props.config.auth} instance={props.msalInstance}>
        <RegistryProvider registry={registry}>
          <SignInFailureContext.Provider value={failureContext}>
            {router !== null && <RouterProvider router={router} />}
          </SignInFailureContext.Provider>
        </RegistryProvider>
      </AuthProvider>
    </RuntimeConfigProvider>
  )
}
